// src/contexts/AuthContext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import i18n from '../i18n/i18n';
import { useBiometric } from '../hooks/useBiometric';

// Define the base URL for all API requests
// Use environment variable with a hardcoded fallback for safety
// In development, use relative /api path to leverage proxy in package.json
// In production, use full URL with /api path
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

console.log('API_BASE_URL:', API_BASE_URL);
console.log('Source:', process.env.REACT_APP_API_URL ? 'Environment Variable' : 'Hardcoded Fallback');

// Create the authentication context
const AuthContext = createContext({
    user: null,
    loading: false,
    error: null,
    initializing: true,
    login: async (credentials) => null,
    logout: async () => {},
    register: async (userData) => null,
    setError: (error) => {},
    clearError: () => {}
});

export const AuthProvider = ({ children }) => {
    // Core state management for authentication
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);

    // Biometric state
    const biometric = useBiometric();
    const [showBiometricLock, setShowBiometricLock] = useState(false);
    const [showBiometricEnroll, setShowBiometricEnroll] = useState(false);
    const [biometricEnrollCredentials, setBiometricEnrollCredentials] = useState(null);
    const [biometricEnrolling, setBiometricEnrolling] = useState(false);
    const skipBiometricRef = useRef(false);
    const hasInitRef = useRef(false);

    // Helper function to validate and clean token
    const validateToken = useCallback((token) => {
        if (!token) return null;
        
        // Check token size (JWT tokens should typically be under 4KB)
        if (token.length > 4000) {
            console.error('Token is too large:', token.length, 'characters');
            localStorage.removeItem('token');
            setUser(null);
            return null;
        }
        
        // Check if token looks valid (basic JWT format check)
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.error('Invalid token format - not a valid JWT');
            localStorage.removeItem('token');
            setUser(null);
            return null;
        }
        
        // Check for suspicious characters that might cause header issues
        if (token.includes('\n') || token.includes('\r') || token.includes('\t')) {
            console.error('Token contains invalid characters');
            localStorage.removeItem('token');
            setUser(null);
            return null;
        }
        
        console.log('Token validated successfully. Size:', token.length, 'characters');
        return token;
    }, []);

    // Helper function to make authenticated API requests
    const makeRequest = useCallback(async (endpoint, options = {}) => {
        // Fix potential path issues by ensuring consistent formatting
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
        const url = `${API_BASE_URL}/${cleanEndpoint}`;
        
        console.log('API_BASE_URL is:', API_BASE_URL);
        console.log('Clean endpoint:', cleanEndpoint);
        console.log('Full URL constructed:', url);
        
        // Get and validate token
        const rawToken = localStorage.getItem('token');
        const token = validateToken(rawToken);
        
        // Prepare headers with size validation
        const baseHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...options.headers
        };
        
        // Only add Authorization header if we have a valid token AND it's not a login/register request
        const isAuthEndpoint = endpoint.includes('login') || endpoint.includes('register');
        const headers = {
            ...baseHeaders,
            ...(token && !isAuthEndpoint && { 'Authorization': `Bearer ${token}` })
        };

        // DEBUG: Check total header size to prevent 431 errors
        const headerString = JSON.stringify(headers);
        console.log('Request headers size:', headerString.length, 'characters');
        
        if (headerString.length > 8000) {
            console.error('Headers too large!', headerString.length, 'characters');
            console.error('Clearing token to prevent 431 error');
            localStorage.removeItem('token');
            setUser(null);
            throw new Error('Request headers too large. Please login again.');
        }

        try {
            console.log('Request details:', { 
                url, 
                method: options.method || 'GET',
                hasBody: !!options.body,
                headerCount: Object.keys(headers).length,
                hasAuthHeader: !!headers.Authorization
            });
            
            const response = await fetch(url, {
                method: options.method || 'GET',
                headers,
                ...options
            });

            // Log response status for debugging
            console.log('Response status:', response.status, response.statusText);

            // Handle 431 error specifically
            if (response.status === 431) {
                console.error('431 Request Header Fields Too Large - clearing all auth data');
                localStorage.removeItem('token');
                setUser(null);
                throw new Error('Request headers too large. Authentication cleared. Please login again.');
            }

            // First get the raw response text for better error handling
            const responseText = await response.text();
            console.log('Response text (preview):', responseText.substring(0, 200));
            
            // Handle empty responses
            if (!responseText.trim()) {
                if (response.ok) {
                    return { success: true, message: 'Operation completed successfully' };
                } else {
                    throw new Error(`Server returned empty response with status ${response.status}`);
                }
            }
            
            // Try to parse the response as JSON
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Response parsing error:', parseError);
                console.error('Raw response:', responseText);
                throw new Error(`Invalid server response: ${responseText.slice(0, 100)}...`);
            }

            // Handle unsuccessful responses
            if (!response.ok) {
                // Special case: email verification required (403 with flag)
                if (response.status === 403 && data.requiresEmailVerification) {
                    return data;
                }
                const errorMessage = data.message || data.error || 'Operation failed';
                throw new Error(errorMessage);
            }

            return data;
            
        } catch (error) {
            console.error(`API request failed for ${endpoint}:`, error);
            
            // Handle specific error types
            if (error.message.includes('Failed to fetch')) {
                throw new Error('Unable to connect to server. Please check your connection.');
            }
            
            if (error.message.includes('431')) {
                localStorage.removeItem('token');
                setUser(null);
                throw new Error('Authentication data corrupted. Please login again.');
            }
            
            throw error;
        }
    }, [validateToken]);

    // Helper function to normalize user data from MongoDB format
    const normalizeUserData = useCallback((userData) => {
        if (!userData || !userData._id) {
            throw new Error('Invalid user data structure');
        }

        return {
            ...userData,
            id: userData._id,
            fullName: userData.name,
            emailVerified: Boolean(userData.emailVerified)
        };
    }, []);

    // Initialize authentication state when the app loads
    useEffect(() => {
        // Prevent double initialization
        if (hasInitRef.current) return;

        // On mobile, wait for biometric check to finish before deciding the auth flow
        if (biometric.isNative && biometric.checking) {
            return; // Still checking biometric availability — wait
        }

        hasInitRef.current = true;

        const initializeAuth = async () => {
            try {
                console.log('=== AUTH INITIALIZATION START ===');

                const token = localStorage.getItem('token');
                console.log('Token found in localStorage:', !!token, token ? `Length: ${token.length}` : 'No token');
                
                if (!token) {
                    console.log('No token found during initialization - user not authenticated');
                    throw new Error('No token found');
                }

                // Validate token before using it
                const validToken = validateToken(token);
                if (!validToken) {
                    throw new Error('Invalid token found during initialization');
                }

                console.log('Initializing auth with token:', token.substring(0, 15) + '...');

                // Attempt to get the current user's information
                const data = await makeRequest('/auth/me', { method: 'GET' });
                
                if (data && data.data && data.data._id) {
                    const normalizedUser = normalizeUserData(data.data);
                    setUser(normalizedUser);
                    
                    // Sync language from server on app init
                    const userLang = normalizedUser?.preferences?.language;
                    if (userLang && i18n.language !== userLang) {
                      i18n.changeLanguage(userLang);
                      localStorage.setItem('soldikeeper-language', userLang);
                    }
                    
                    console.log('Auth initialization successful - user:', { email: normalizedUser.email, isAdmin: normalizedUser.isAdmin });
                } else {
                    console.log('Invalid user data received during initialization');
                    throw new Error('Invalid user data received');
                }
                
            } catch (err) {
                console.log('Auth initialization error:', err.message);
                console.log('Clearing token and setting user to null');
                localStorage.removeItem('token');
                setUser(null);
            } finally {
                console.log('=== AUTH INITIALIZATION END ===');
                setInitializing(false);
                setLoading(false);
            }
        };

        initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizeUserData, makeRequest, validateToken, biometric.checking]);
    
    // Handle user registration
    const register = async (userData) => {
        try {
            setLoading(true);
            setError(null);

            console.log('Attempting registration with email:', userData.email);

            // Add client-side validation
            if (!userData.name || !userData.email || !userData.password) {
                throw new Error('Please provide all required fields');
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(userData.email)) {
                throw new Error('Please provide a valid email address');
            }

            // Validate password strength
            if (userData.password.length < 6) {
                throw new Error('Password must be at least 6 characters long');
            }

            // Make the registration request
            const data = await makeRequest('/auth/register', {
                method: 'POST',
                body: JSON.stringify({
                    name: userData.name.trim(),
                    email: userData.email.toLowerCase().trim(),
                    password: userData.password
                })
            });

            console.log('Registration successful:', {
                hasUser: !!data.data?.user,
                hasToken: !!data.data?.token
            });

            // Handle email verification required response (new flow)
            if (data.requiresEmailVerification) {
                return { requiresEmailVerification: true, email: data.data?.email, name: data.data?.name };
            }

            // Process successful registration (legacy: token already returned)
            if (!data.data?.user || !data.data?.token) {
                throw new Error('Invalid registration response from server');
            }

            const normalizedUser = normalizeUserData(data.data.user);
            
            // Validate token before storing
            const validatedToken = validateToken(data.data.token);
            if (!validatedToken) {
                throw new Error('Received invalid token from server');
            }
            
            localStorage.setItem('token', validatedToken);
            setUser(normalizedUser);

            return normalizedUser;

        } catch (err) {
            const errorMessage = err.message || 'An error occurred during registration';
            console.error('Registration error:', errorMessage);
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Handle user login
    const login = async (credentials) => {
        try {
            setLoading(true);
            setError(null);

            console.log('Attempting login with email:', credentials.email, 'Remember Me:', !!credentials.rememberMe);

            // Validate credentials
            if (!credentials.email || !credentials.password) {
                throw new Error('Please provide both email and password');
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(credentials.email)) {
                throw new Error('Please provide a valid email address');
            }

            // Clear any existing corrupted token before login
            localStorage.removeItem('token');
            setUser(null);

            // Make the login request (without Authorization header)
            const data = await makeRequest('/auth/login', {
                method: 'POST',
                body: JSON.stringify({
                    email: credentials.email.toLowerCase().trim(),
                    password: credentials.password,
                    rememberMe: credentials.rememberMe || false
                }),
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                    // Explicitly no Authorization header for login
                }
            });

            console.log('Login successful, received data:', {
                hasUser: !!data.data?.user,
                hasToken: !!data.data?.token,
                rememberMe: credentials.rememberMe
            });

            // Handle email verification required
            if (data.requiresEmailVerification) {
                return { requiresEmailVerification: true, email: data.email };
            }

            // Process successful login
            if (!data.data?.user || !data.data?.token) {
                throw new Error('Invalid login response from server');
            }

            const normalizedUser = normalizeUserData(data.data.user);
            
            // Validate token before storing
            const validatedToken = validateToken(data.data.token);
            if (!validatedToken) {
                throw new Error('Received invalid token from server');
            }
            
            localStorage.setItem('token', validatedToken);
            
            // Store Remember Me preference
            if (credentials.rememberMe) {
                localStorage.setItem('rememberMe', 'true');
                console.log('Remember Me enabled - token will persist for 30 days');
            } else {
                localStorage.removeItem('rememberMe');
            }
            
            setUser(normalizedUser);

            // Sync language preference from user profile on login
            const userLang = normalizedUser?.preferences?.language;
            if (userLang && i18n.language !== userLang) {
              i18n.changeLanguage(userLang);
              localStorage.setItem('soldikeeper-language', userLang);
            }

            return normalizedUser;

        } catch (err) {
            const errorMessage = err.message || 'An error occurred during login';
            console.error('Login error:', errorMessage);
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Handle biometric login (called from LoginForm)
    const loginWithBiometric = async () => {
        try {
            setLoading(true);
            setError(null);
            const credentials = await biometric.authenticateWithBiometric();
            // Biometric IS the "remember me" — always issue a long-lived token
            const result = await login({ ...credentials, rememberMe: true });
            return result;
        } catch (err) {
            console.error('Biometric login failed:', err.message);
            // Biometric key invalidated (user changed fingerprints on device) — clear enrollment
            // so the app doesn't keep trying a broken biometric on every launch.
            const msg = err.message || '';
            if (
                msg.includes('KeyPermanentlyInvalidatedException') ||
                msg.includes('permanently invalidated') ||
                msg.includes('key has been permanently invalidated') ||
                msg.includes('Biometric data has changed') ||
                msg.includes('invalidated')
            ) {
                await biometric.disableBiometric();
                const invalidationError = new Error(
                    'Your fingerprint data changed. Please log in with your password to re-enable fingerprint login.'
                );
                invalidationError.biometricInvalidated = true;
                throw invalidationError;
            }
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Dismiss biometric lock — no longer used, kept for API compat
    const dismissBiometricLock = () => {
        skipBiometricRef.current = true;
    };

    // Prompt enrollment after a successful password login
    const promptBiometricEnroll = (email, password) => {
        if (biometric.isNative && !biometric.isEnabled) {
            setBiometricEnrollCredentials({ email, password });
            setShowBiometricEnroll(true);
        }
    };

    // Complete biometric enrollment
    const completeBiometricEnroll = async () => {
        if (!biometricEnrollCredentials) return;
        setBiometricEnrolling(true);
        try {
            await biometric.enrollBiometric(
                biometricEnrollCredentials.email,
                biometricEnrollCredentials.password
            );
            console.log('Biometric enrollment successful');
            setShowBiometricEnroll(false);
            setBiometricEnrollCredentials(null);
        } catch (err) {
            console.error('Biometric enrollment failed:', err.message);
            // Keep dialog open so user sees it didn't work — they can retry or skip
            alert(err.message || 'Biometric setup failed. Make sure fingerprint is set up in your device settings.');
        } finally {
            setBiometricEnrolling(false);
        }
    };

    // Skip biometric enrollment
    const skipBiometricEnroll = () => {
        setShowBiometricEnroll(false);
        setBiometricEnrollCredentials(null);
    };

    // Handle user logout
    const logout = async () => {
        try {
            setLoading(true);
            console.log('Attempting logout');
            
            // Attempt server-side logout (but don't fail if it doesn't work)
            try {
                await makeRequest('/auth/logout', { method: 'POST' });
                console.log('Server-side logout successful');
            } catch (error) {
                console.warn('Server logout failed (continuing with local logout):', error.message);
            }

            // Clean up local authentication state
            localStorage.removeItem('token');
            skipBiometricRef.current = false; // Reset so biometric shows on next launch
            setUser(null);
            setError(null);
            console.log('Local logout completed');
            
        } catch (err) {
            console.error('Logout error:', err);
            // Force cleanup even if logout fails
            localStorage.removeItem('token');
            setUser(null);
            setError(null);
        } finally {
            setLoading(false);
        }
    };

    // Helper to clear any existing errors
    const clearError = () => setError(null);

    // Debug function to check current auth state
    const debugAuthState = useCallback(() => {
        const token = localStorage.getItem('token');
        console.log('=== AUTH DEBUG ===');
        console.log('User:', user);
        console.log('Token exists:', !!token);
        console.log('Token length:', token?.length);
        console.log('Loading:', loading);
        console.log('Error:', error);
        console.log('Initializing:', initializing);
        console.log('================');
    }, [user, loading, error, initializing]);

    // Add debug function to window for easy access
    useEffect(() => {
        window.debugAuth = debugAuthState;
        return () => {
            delete window.debugAuth;
        };
    }, [user, loading, error, initializing, debugAuthState]);

    // Stable setToken function for OAuth to prevent infinite render loops
    const setTokenWrapper = useCallback((token) => {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    }, []);

    // Prepare the context value with all necessary data and functions
    const value = {
        user,
        error,
        loading,
        isLoading: loading,  // Alias for backward compatibility
        isAuthenticated: !!user && !error,  // Boolean indicating if user is logged in
        initializing,
        login,
        logout,
        register,
        setError,
        clearError,
        setUser, // Add setUser for OAuth (useState setter is already stable)
        setToken: setTokenWrapper, // Use stable callback reference
        debugAuthState, // Include debug function
        // Biometric
        biometric,
        showBiometricLock,
        showBiometricEnroll,
        biometricEnrolling,
        loginWithBiometric,
        dismissBiometricLock,
        promptBiometricEnroll,
        completeBiometricEnroll,
        skipBiometricEnroll,
    };

    // Show loading state while checking initial authentication
    if (initializing) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <div>Loading authentication...</div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook for consuming the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthProvider;